import BaseComponent from '../base-component/base-component'
import type { Nullable } from '../../types/common'
import type Accordeon from '../accordeon/accordeon'
import type Map from '../map-component/index'
import type { ILocationData } from '../map-component/types'

class StoreLocator extends BaseComponent {
  mapSelector: string
  storeElementSelector: string
  storeIdSelector: string
  storeNameSelector: string
  storeCoordinatesSelector: string
  defaultImageSelector: string

  map: Nullable<Map>
  stores: Accordeon[]

  constructor() {
    super()

    this.mapSelector = '#map'
    this.storeElementSelector = 'data-store'
    this.storeIdSelector = 'data-block-id'
    this.storeNameSelector = 'data-name'
    this.storeCoordinatesSelector = 'data-coordinates'
    this.defaultImageSelector = 'map-default-image'

    this.map = this.querySelector(this.mapSelector)
    this.stores = Array.from(
      this.querySelectorAll(`[${this.storeElementSelector}]`),
    )
  }

  connectedCallback(): void {
    if (this.map) {
      this.map.locationsData = this.generateLocationsData()
      this.map.initMap()

      this.map.subscribe('markerClick', this.handleMarkerClick)
      this.map.subscribe('mapError', this.handleMapError)
    }

    this.stores.forEach(store => {
      store.addEventListener('click', this.handleStoreClick)
    })
  }

  disconnectedCallback(): void {
    if (this.map) {
      this.map.unsubscribe('markerClick', this.handleMarkerClick)
      this.map.unsubscribe('mapError', this.handleMapError)
    }

    this.stores.forEach(store => {
      store.removeEventListener('click', this.handleStoreClick)
    })
  }

  generateLocationsData = (): ILocationData[] => {
    const locationsData = this.stores.map(store => ({
      coordinates: store.getAttribute(this.storeCoordinatesSelector),
      id: store.getAttribute(this.storeIdSelector),
    }))

    return locationsData
  }

  handleMarkerClick = ({ markerId }: { markerId: string }): void => {
    const storeToScrollTo = this.stores.find(
      store => store.dataset.blockId === markerId,
    )

    storeToScrollTo?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    storeToScrollTo?.show()

    this.toggleStoreVisibility(markerId)
  }

  handleStoreClick = (event): void => {
    const storeId = event.currentTarget.getAttribute(this.storeIdSelector)

    this.toggleStoreVisibility(storeId)
  }

  toggleStoreVisibility = (id: string): void => {
    this.toggleStoreExpanded(id)
    this.toggleMarkerHighlighted(id)
  }

  toggleStoreExpanded = (id: string): void => {
    this.stores.forEach(store => {
      if (store.dataset.blockId !== id && store.isExpanded) {
        store.hide()
      }
    })
  }

  toggleMarkerHighlighted = (id: string): void => {
    const isAnyStoreExpanded = this.stores.find(store => store.isExpanded)

    if (isAnyStoreExpanded) {
      this.map?.highlightMarker(id)
    } else {
      this.map?.highlightMarker(null)
    }
  }

  setDefaultImage = (): void => {
    const image = this.querySelector(`[${this.defaultImageSelector}]`)?.content

    this.map?.replaceWith(image)
  }

  handleMapError = (): void => {
    this.setDefaultImage()
  }
}

export default StoreLocator
